import React from "react";
import { Label } from "@/components/ui/label";
import { BODY_PARTS, PRIMARY_MUSCLES, BODY_PART_COLOR } from "@/lib/anatomy";

/**
 * Two-select row: body part + primary muscle. Both optional.
 *
 * Renders a tiny "Anatomy" section with two native <select> boxes so we don't
 * need to build a headless combobox every time. The empty option keeps
 * legacy movements uncategorised.
 */
export function AnatomyFields({ bodyPart, primaryMuscle, onChange }) {
  return (
    <div className="grid grid-cols-2 gap-3" data-testid="anatomy-fields">
      <div>
        <Label className="hs-label">Body part</Label>
        <select
          value={bodyPart || ""}
          onChange={(e) => onChange({ body_part: e.target.value || null, primary_muscle: primaryMuscle || null })}
          className="mt-2 h-11 w-full rounded-xl border border-line bg-white px-3 text-sm"
          data-testid="anatomy-body-part"
        >
          <option value="">— unset —</option>
          {BODY_PARTS.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
      </div>
      <div>
        <Label className="hs-label">Primary muscle</Label>
        <select
          value={primaryMuscle || ""}
          onChange={(e) => onChange({ body_part: bodyPart || null, primary_muscle: e.target.value || null })}
          className="mt-2 h-11 w-full rounded-xl border border-line bg-white px-3 text-sm"
          data-testid="anatomy-primary-muscle"
        >
          <option value="">— unset —</option>
          {PRIMARY_MUSCLES.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>
    </div>
  );
}

/**
 * A horizontally-scrollable row of pill buttons — one per body part plus an "All" pill.
 * Filters lists of movements/exercises by body_part. Selection is single-value.
 */
export function BodyPartFilterPills({ value, onChange, counts, testIdPrefix = "body-part-filter" }) {
  const cellClass = (active) =>
    `px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-[0.12em] transition-colors whitespace-nowrap ${
      active ? "bg-moss text-white" : "bg-muted2 text-ink-2 hover:bg-sage hover:text-moss"
    }`;
  const total = counts ? Object.values(counts).reduce((a, b) => a + b, 0) : null;

  return (
    <div className="flex flex-wrap gap-2" data-testid={`${testIdPrefix}-row`}>
      <button
        type="button"
        onClick={() => onChange(null)}
        className={cellClass(!value)}
        data-testid={`${testIdPrefix}-all`}
      >
        All{total != null ? ` · ${total}` : ""}
      </button>
      {BODY_PARTS.map((b) => {
        const count = counts ? (counts[b] || 0) : null;
        if (counts && count === 0) return null;
        return (
          <button
            key={b}
            type="button"
            onClick={() => onChange(b)}
            className={cellClass(value === b)}
            data-testid={`${testIdPrefix}-${b.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {b}{count != null ? ` · ${count}` : ""}
          </button>
        );
      })}
    </div>
  );
}

/** Small inline chip showing a movement's body-part classification. */
export function BodyPartChip({ bodyPart, primaryMuscle }) {
  if (!bodyPart && !primaryMuscle) return null;
  const color = bodyPart ? (BODY_PART_COLOR[bodyPart] || "bg-muted2 text-ink-2") : "bg-muted2 text-ink-2";
  return (
    <span
      className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-[0.15em] ${color}`}
      data-testid="body-part-chip"
      title={primaryMuscle ? `Primary muscle: ${primaryMuscle}` : undefined}
    >
      {bodyPart || "—"}
      {primaryMuscle ? ` · ${primaryMuscle}` : ""}
    </span>
  );
}
